<!-- Skeleton -->
<div *ngIf="isLoadingEvent">
  <div class="bg-[var(--tertiary)] py-12">
    <div class="container-padding mx-auto max-w-screen-desktop">
      <app-skeleton dimensions="h-10 w-1/2"></app-skeleton>
    </div>
  </div>
  <div class="bg-[var(--light-grey)] py-10">
    <div class="container-padding mx-auto max-w-screen-desktop">
      <div class="grid grid-cols-1 tablet:grid-cols-2 gap-6">
        <app-skeleton dimensions="w-full rounded-2xl" style="aspect-ratio:16/9;display:block;"></app-skeleton>
        <app-skeleton dimensions="w-full rounded-2xl" style="aspect-ratio:16/9;display:block;"></app-skeleton>
      </div>
    </div>
  </div>
</div>

<!-- Event Content -->
<div *ngIf="!isLoadingEvent && event">

  <!-- ═══ Header: Brown bg, no image ═══ -->
  <div class="bg-[var(--tertiary)] py-8 mobile-m:py-10">
    <div class="container-padding mx-auto max-w-screen-desktop w-full">
      <div class="flex flex-col mobile-m:flex-row mobile-m:justify-between mobile-m:items-center gap-2">
        <h1 class="uppercase text-[var(--beige)] text-[36px]/[36px] mobile-m:text-[40px]/[40px] font-normal m-0">{{event.title}}</h1>
        <p class="uppercase text-[var(--yellow)] text-[18px]/[18px] mobile-m:text-[21px]/[21px] font-bold m-0">{{event.eventType.name}}</p>
      </div>
    </div>
  </div>

  <!-- ═══ Content ═══ -->
  <div class="bg-[var(--light-grey)] py-8 pb-12">
    <div class="container-padding mx-auto max-w-screen-desktop">

      <!-- Main row: Left (info + image) | Right (map, same total height) -->
      <div class="grid grid-cols-1 tablet:grid-cols-2 gap-6 mb-8 tablet:items-stretch">

        <!-- Left column: Purchase info + Event image -->
        <div class="flex flex-col gap-5">
          <!-- Purchase info -->
          <div class="flex items-center justify-between gap-4">
            <div class="flex flex-col gap-1">
              <div class="flex items-center gap-2">
                <fa-icon [icon]="faCalendarAlt" class="text-[var(--secondary)]"></fa-icon>
                <span class="text-[13px] font-medium text-[var(--black)]">{{ 'event.validUntil' | translate }} {{ event.date | date: "dd MMM yyyy" }}</span>
              </div>
              <div class="flex items-center gap-3">
                <ng-container *ngIf="event.discountAmount > 0; else priceOnly">
                  <span class="text-[18px] font-bold text-[var(--dark-grey)] line-through">{{event.price | currency}}</span>
                  <span class="text-[26px] font-bold text-[var(--dark-orange)]">{{event.price - event.discountAmount | currency}}</span>
                </ng-container>
                <ng-template #priceOnly>
                  <span class="text-[26px] font-bold text-[var(--dark-orange)]">{{event.price | currency}}</span>
                </ng-template>
              </div>
            </div>
            <button (click)="addToCart($event)"
              class="flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--secondary)] text-white font-bold text-[12px] uppercase shadow-[0_4px_0_0_var(--dark-orange)] hover:shadow-[0_6px_0_0_var(--dark-orange)] hover:-translate-y-1 active:translate-y-1 active:shadow-[0_2px_0_0_var(--dark-orange)] transition-all whitespace-nowrap">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z"/>
              </svg>
              {{ 'event.addToCart' | translate }}
            </button>
          </div>

          <!-- Event image (takes remaining height) -->
          <div class="rounded-2xl overflow-hidden shadow-md" style="aspect-ratio: 16/9;">
            <img [src]="event.imageUrl" [alt]="event.title" class="w-full h-full object-cover">
          </div>
        </div>

        <!-- Right column: Map (stretches to match left column height) -->
        <div class="flex flex-col">
          <h3 class="uppercase text-[var(--black)] font-bold text-[16px] mb-2">{{ 'event.locations' | translate }}</h3>
          <div class="flex-1 min-h-[280px] rounded-2xl overflow-hidden border-2 border-[var(--grey)] shadow-sm">
            <app-map
              [mapHeight]="'100%'"
              [eventBranches]="event.eventBranches || []"
              [customCoords]="event.locationType == 2 ? [event.latitude, event.longitude] : undefined"
              [customTitle]="event.locationType == 2 ? event.title : undefined"
              [customLogoUrl]="event.locationType == 2 ? event.imageUrl : undefined"
              [customGooglePlaceId]="event.locationType == 2 ? event.google_place_id : undefined"
              class="w-full h-full block">
            </app-map>
          </div>
        </div>

      </div>

      <!-- Description -->
      <div class="mb-6">
        <h3 class="uppercase text-[var(--black)] font-bold text-[16px] mb-2">{{ 'event.description' | translate }}</h3>
        <p [style.white-space]="'pre-line'" class="text-[14px]/[22px] text-[var(--black)] leading-relaxed">{{event.description}}</p>
      </div>

      <!-- Participants -->
      <div *ngIf="event.eventBranches && event.eventBranches.length > 0" class="mb-6">
        <h3 class="uppercase text-[var(--dark-orange)] font-bold text-[14px] mb-3">{{ 'event.participants' | translate }}</h3>
        <div class="flex flex-wrap gap-3">
          <div *ngFor="let eventBranch of event.eventBranches" class="w-16 h-16 tablet:w-20 tablet:h-20">
            <app-brewery-logo [brewery]="eventBranch.branch?.brewery"></app-brewery-logo>
          </div>
        </div>
      </div>

      <!-- Schedules (only if any branch has them) -->
      <div *ngIf="eventHasAnySchedules()" class="mb-6">
        <h3 class="uppercase text-[var(--black)] font-bold text-[14px] mb-2 flex items-center gap-2">
          <fa-icon [icon]="faClock" class="text-[var(--dark-orange)]"></fa-icon>
          {{ 'event.schedules' | translate }}
        </h3>
        <div class="flex flex-col">
          <ng-container *ngFor="let eventBranch of event.eventBranches">
            <ng-container *ngIf="branchHasSchedules(eventBranch.branch)">
              <button class="w-full flex items-center justify-between py-2.5 text-left group"
                      (click)="toggleSchedule(eventBranch.branch!.id)">
                <span class="text-[12px] font-semibold uppercase tracking-wide text-[var(--black)]">{{ eventBranch.branch?.brewery?.name }}</span>
                <span class="text-[10px] uppercase tracking-widest text-[var(--dark-grey)] group-hover:text-[var(--black)] transition-colors">
                  {{ expandedScheduleBranchId === eventBranch.branch!.id ? ('schedule.hide' | translate) : ('schedule.viewHours' | translate) }}
                </span>
              </button>
              <div *ngIf="expandedScheduleBranchId === eventBranch.branch!.id" class="pb-3">
                <app-branch-schedule
                  [schedules]="eventBranch.branch?.schedules || []"
                  [branchAddress]="eventBranch.branch?.address || ''"
                  [showDrinkHoppingTip]="true">
                </app-branch-schedule>
              </div>
              <div class="w-full h-px bg-[var(--grey)]"></div>
            </ng-container>
          </ng-container>
        </div>
      </div>

      <!-- Disclaimer -->
      <p class="text-[11px] text-[var(--dark-grey)] leading-relaxed">
        <span class="font-semibold">{{ 'event.disclaimer' | translate }}</span>
        {{ 'event.disclaimerFull' | translate }}
      </p>
    </div>
  </div>

  <!-- ═══ Other events (swiper) ═══ -->
  <div *ngIf="otherEvents.length > 0" class="bg-[var(--light-grey)] pb-12">
    <div class="container-padding mx-auto max-w-screen-desktop">
      <h3 class="uppercase text-[var(--dark-orange)] font-bold text-[22px] pb-4">{{ 'event.otherEvents' | translate }}</h3>
    </div>
    <div class="container-padding mx-auto max-w-screen-desktop">
      <div class="relative">
        <div class="overflow-visible py-2">
          <div class="grid grid-cols-1 mobile-m:grid-cols-2 tablet:grid-cols-3 gap-4">
            <div *ngFor="let otherEvent of getVisibleEvents()" class="relative" style="aspect-ratio: 16/9;">
              <div *ngIf="activeOtherMapId === otherEvent.id" class="absolute inset-0 rounded-xl overflow-hidden border-2 border-[var(--grey)]">
                <app-map
                  [eventBranches]="otherEvent.eventBranches || []"
                  [customCoords]="otherEvent.locationType == 2 ? [otherEvent.latitude, otherEvent.longitude] : undefined"
                  [customTitle]="otherEvent.title"
                  [compact]="true"
                  [enableClustering]="false"
                  mapHeight="100%"
                  class="w-full h-full block">
                </app-map>
                <div class="absolute top-2 right-2 w-8 h-8 rounded-lg bg-white/90 flex items-center justify-center cursor-pointer z-10 text-[var(--tertiary)] hover:bg-[var(--bordeaux)] hover:text-white transition-colors"
                  (click)="activeOtherMapId = null">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                </div>
              </div>
              <app-event-card *ngIf="activeOtherMapId !== otherEvent.id" [event]="otherEvent" [cardMode]="true" (mapClick)="activeOtherMapId = $event"></app-event-card>
            </div>
          </div>
        </div>

        <ng-container *ngIf="otherEvents.length > visibleCount">
          <button class="absolute top-1/2 -left-2 mobile-m:-left-4 -translate-y-1/2 w-12 h-12 mobile-m:w-10 mobile-m:h-10 rounded-full bg-white border-2 border-[var(--grey)] shadow-md flex items-center justify-center hover:bg-[var(--light-grey)] transition-colors z-10"
            (click)="swiperPrev()">
            <svg class="w-6 h-6 mobile-m:w-5 mobile-m:h-5 text-[var(--tertiary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <button class="absolute top-1/2 -right-2 mobile-m:-right-4 -translate-y-1/2 w-12 h-12 mobile-m:w-10 mobile-m:h-10 rounded-full bg-white border-2 border-[var(--grey)] shadow-md flex items-center justify-center hover:bg-[var(--light-grey)] transition-colors z-10"
            (click)="swiperNext()">
            <svg class="w-6 h-6 mobile-m:w-5 mobile-m:h-5 text-[var(--tertiary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </ng-container>
      </div>

      <div *ngIf="otherEvents.length > visibleCount" class="mt-4 flex justify-center gap-1.5">
        <div *ngFor="let p of [].constructor(totalPages); let i = index"
          class="h-[6px] rounded-full transition-all duration-300"
          [ngClass]="i === swiperIndex ? 'w-6 bg-[var(--secondary)]' : 'w-[6px] bg-[var(--grey)]'">
        </div>
      </div>
    </div>
  </div>
</div>
